import { BriefDeliverableTaskTypeEnum } from "@entities/brief/lib/constants";
import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Button, Modal } from "@shared/ui";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  countMatchingCreators,
  hasBriefHtml,
  listMatchingDeliverableTasks,
  type AttachTaskCreator,
} from "../lib/attachTask";
import { useAttachEditorContent } from "../lib/useAttachEditorContent";
import { AttachBriefEditorStep } from "./AttachBriefEditorStep";
import { AttachTaskChrome } from "./AttachTaskChrome";
import { AttachTaskDeliverableStep } from "./AttachTaskDeliverableStep";
import styles from "./AttachTaskModal.module.scss";

interface AttachBriefModalProps {
  open: boolean;
  selectedCount: number;
  creators: AttachTaskCreator[];
  confirmLoading?: boolean;
  onClose: () => void;
  onConfirm: (taskId: string, title: string) => void;
}

export function AttachBriefModal({
  open,
  selectedCount,
  creators,
  confirmLoading,
  onClose,
  onConfirm,
}: AttachBriefModalProps) {
  const { briefId } = useBriefStore();
  const { config } = useGetBriefDeliverableConfig(briefId || "");
  const editorRef = useRef<EditorHandle>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [taskId, setTaskId] = useState<string>();
  const [editorKey, setEditorKey] = useState(0);
  const {
    hasContent,
    setHasContent,
    isMediaUploading,
    resetContentState,
    syncContent,
    handleImportComplete,
    handleMediaUploadStart,
    handleMediaUploadComplete,
  } = useAttachEditorContent(editorRef);

  const deliverableOptions = useMemo(
    () => listMatchingDeliverableTasks(config, BriefDeliverableTaskTypeEnum.Brief),
    [config]
  );

  const selectedDeliverable = deliverableOptions.find(
    (option) => option.taskId === taskId
  );
  const canMeasureSkip =
    creators.length > 0 && creators.length === selectedCount;
  const loadedMatchingCount = selectedDeliverable
    ? countMatchingCreators(
        creators,
        selectedDeliverable.deliverableConfigRefId
      )
    : 0;
  const skippedCount = canMeasureSkip
    ? Math.max(0, creators.length - loadedMatchingCount)
    : 0;  useEffect(() => {
    if (!open) {
      setStep(1);
      setTaskId(undefined);
      resetContentState();
      return;
    }
    setStep(1);
    setHasContent(false);
    setEditorKey((key) => key + 1);
  }, [open]);

  useEffect(() => {
    if (!open || taskId || deliverableOptions.length !== 1) return;
    setTaskId(deliverableOptions[0].taskId);
  }, [open, deliverableOptions, taskId]);

  const isBusy = Boolean(confirmLoading) || isMediaUploading;
  const canContinue =
    Boolean(taskId) && (!canMeasureSkip || loadedMatchingCount > 0);

  const handleAttach = () => {
    if (!taskId) return;
    const html = editorRef.current?.getHTML({ syncTokens: true }) ?? "";
    if (!hasBriefHtml(html)) return;
    onConfirm(taskId, html);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={680}
      zIndex={1100}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
        footer: styles.modalFooter,
      }}
      title={
        <div className={styles.titleBlock}>
          <span className={styles.title}>Attach brief</span>
          <span className={styles.subtitle}>
            {step === 1
              ? "Choose which deliverable this brief applies to."
              : "Write the brief, then attach it to the selected creators."}
          </span>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <div>
            {step === 2 ? (
              <Button
                type="text"
                onClick={() => setStep(1)}
                className={styles.backButton}
              >
                <ArrowLeft size={14} />
                Back
              </Button>
            ) : null}
          </div>
          <div className={styles.footerActions}>
            <Button onClick={onClose}>Cancel</Button>
            {step === 1 ? (
              <Button
                type="primary"
                disabled={!canContinue}
                onClick={() => {
                  setEditorKey((key) => key + 1);
                  setHasContent(false);
                  setStep(2);
                }}
                className={styles.primaryButton}
              >
                Continue
                <ArrowRight size={14} />
              </Button>
            ) : (
              <Button
                type="primary"
                loading={isBusy}
                disabled={!taskId || isBusy || !hasContent}
                onClick={handleAttach}
                className={styles.primaryButton}
              >
                Attach to {selectedCount} creator
                {selectedCount === 1 ? "" : "s"}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <AttachTaskChrome
        step={step}
        selectedCount={selectedCount}
        creators={creators}
        stepTwoLabel="Brief"
      />

      {step === 1 ? (
        <AttachTaskDeliverableStep
          options={deliverableOptions}
          selectedTaskId={taskId}
          creators={creators}
          selectedCount={selectedCount}
          skippedCount={skippedCount}
          onSelect={setTaskId}
          emptyLabel="No brief tasks are configured for this campaign."
          skipActionLabel="Brief"
        />
      ) : (
        <AttachBriefEditorStep
          editorRef={editorRef}
          editorKey={editorKey}
          deliverableName={selectedDeliverable?.name || ""}
          attachCount={selectedCount}
          onContentChange={syncContent}
          onMediaUploadStart={handleMediaUploadStart}
          onMediaUploadComplete={handleMediaUploadComplete}
          onImportComplete={handleImportComplete}
        />
      )}
    </Modal>
  );
}
